import React, { useEffect, useState } from "react";
import { Box, TextField, Typography, IconButton } from "@mui/material";
import { DragIndicator as DragIcon } from "@mui/icons-material";
import { type TabData } from "@utils/dashboard-storage";
import {
  type DeleteConfirmationState,
  type DraggedItem,
  type DropPointer,
} from "@/hooks/tab-management/types";
import HPButton from "@/components/common/Button";
import { WarningIconBlack, RemoveIcon, RemoveIconRed } from "@/assets/images";
import TabRow from "./TabRow";

interface DnDHandlers {
  handleDragStart: (e: React.DragEvent, item: DraggedItem) => void;
  handleDragEnd: () => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragEnter: (e: React.DragEvent, target: DropPointer) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

interface GroupContainerProps {
  groupName: string;
  tabs: TabData[];
  navIndex: number;
  dragged: DraggedItem | null;
  dragOverTarget: DropPointer | null;
  isDeleteConfirming: boolean;
  tabErrors: Record<string, string>;
  hoveredTabId: string | null;
  deleteConfirmation: DeleteConfirmationState | null;
  isDuplicatingTab: boolean;
  canDeleteTab: boolean;
  canDuplicateTab: boolean;
  dnd: DnDHandlers;
  /** Attempts a rename; returns a validation error message, or null on success. */
  onRenameGroup: (oldName: string, newName: string) => string | null;
  onDeleteGroupClick: () => void;
  onConfirmDeleteGroup: () => void;
  onCancelDeleteGroup: () => void;
  onTabNameChange: (tabId: string, value: string) => void;
  onTabNameBlur: (tabId: string, value: string) => void;
  onDeleteTabClick: (tabId: string) => void;
  onDuplicateTabClick: (tabId: string, label: string) => void;
  onConfirmDeleteTab: () => void;
  onCancelDeleteTab: () => void;
  onTabMouseEnter: (tabId: string) => void;
  onTabMouseLeave: () => void;
}

const GroupContainer: React.FC<GroupContainerProps> = ({
  groupName,
  tabs,
  navIndex,
  dragged,
  dragOverTarget,
  isDeleteConfirming,
  tabErrors,
  hoveredTabId,
  deleteConfirmation,
  isDuplicatingTab,
  canDeleteTab,
  canDuplicateTab,
  dnd,
  onRenameGroup,
  onDeleteGroupClick,
  onConfirmDeleteGroup,
  onCancelDeleteGroup,
  onTabNameChange,
  onTabNameBlur,
  onDeleteTabClick,
  onDuplicateTabClick,
  onConfirmDeleteTab,
  onCancelDeleteTab,
  onTabMouseEnter,
  onTabMouseLeave,
}) => {
  const [draftName, setDraftName] = useState(groupName);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  useEffect(() => {
    setDraftName(groupName);
  }, [groupName]);

  const handleNameBlur = () => {
    const error = onRenameGroup(groupName, draftName);
    if (error) {
      setRenameError(error);
      setDraftName(groupName);
    } else {
      setRenameError(null);
    }
  };

  const firstMemberId = tabs[0]?.id ?? null;
  const isDraggingThisGroup =
    dragged?.type === "group" && dragged.groupName === groupName;
  // Dragging a whole group can only ever land before/after another group as
  // one block (see handleDrop) — never spliced between two of its member
  // tabs — so while a group is being dragged, this whole container is a
  // single drop target instead of exposing per-tab insertion points.
  const isDraggingGroup = dragged?.type === "group";
  const isThisGroupTargeted = isDraggingGroup && dragOverTarget?.navIndex === navIndex;
  const isDraggedOverHeader =
    !isDraggingGroup &&
    !!firstMemberId &&
    dragOverTarget?.tabId === firstMemberId &&
    dragOverTarget.groupName === null;
  const isDraggedOverTrailingZone =
    !isDraggingGroup &&
    dragOverTarget?.groupName === groupName &&
    dragOverTarget.tabId === null;

  return (
    <Box sx={{ mb: "10px" }}>
      {/* Insertion line: dropping here places the dragged tab before this group */}
      <Box
        sx={{
          height: 0,
          mb: "4px",
          borderTop: isDraggedOverHeader ? "3px dashed #1976d2" : "3px dashed transparent",
        }}
      />
      <Box
        sx={{
          border: isThisGroupTargeted ? "2px solid #1976d2" : "1px solid black",
          borderRadius: "8px",
          opacity: isDraggingThisGroup ? 0.5 : 1,
        }}
      >
        <Box
          draggable={!isDeleteConfirming}
          onDragStart={(e) =>
            dnd.handleDragStart(e, { type: "group", groupName })
          }
          onDragEnd={dnd.handleDragEnd}
          onDragOver={dnd.handleDragOver}
          onDragEnter={(e) =>
            dnd.handleDragEnter(e, {
              navIndex,
              tabId: firstMemberId,
              groupName: null,
            })
          }
          onDragLeave={dnd.handleDragLeave}
          onDrop={dnd.handleDrop}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            paddingTop: "10px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            paddingRight: "16px",
            cursor: isDeleteConfirming ? "default" : "grab",
          }}
        >
          {isDeleteConfirming ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexGrow: 1,
              }}
            >
              <WarningIconBlack />
              <Typography
                variant="body2"
                sx={{ flexGrow: 1, fontSize: "14px" }}
              >
                Deleting this group will retain its tabs and their content as
                ungrouped tabs.
              </Typography>
              <HPButton
                variant="text"
                onClick={onConfirmDeleteGroup}
                className="px-[0px] font-bold"
                color="error"
              >
                Delete
              </HPButton>
              <HPButton
                variant="text"
                onClick={onCancelDeleteGroup}
                className="px-[0px] font-bold"
              >
                Cancel
              </HPButton>
            </Box>
          ) : (
            <>
              <DragIcon sx={{ mr: 2, color: "text.secondary" }} />
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  size="small"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={handleNameBlur}
                  inputProps={{
                    maxLength: 40,
                    style: { fontWeight: 600 },
                  }}
                  fullWidth
                  error={!!renameError}
                  helperText={renameError}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black",
                      },
                    },
                  }}
                />
              </Box>
              <IconButton
                aria-label="delete group"
                onClick={onDeleteGroupClick}
                onMouseEnter={() => setIsDeleteHovered(true)}
                onMouseLeave={() => setIsDeleteHovered(false)}
                sx={{ ml: 1, color: "text.secondary", "&:hover": { color: "#D80000" } }}
                title="Delete group"
              >
                {isDeleteHovered ? <RemoveIconRed /> : <RemoveIcon />}
              </IconButton>
              {/* Reserves the same width as a tab row's duplicate button, so
                  the delete icon lands at the same x position as tab rows'. */}
              <Box sx={{ width: 40, ml: 1 }} />
            </>
          )}
        </Box>

        <Box>
          {tabs.map((tab) => (
            <TabRow
              key={tab.id}
              tab={tab}
              isDragging={dragged?.type === "tab" && dragged.tabId === tab.id}
              isDraggedOver={
                !isDraggingGroup &&
                !(dragged?.type === "tab" && dragged.tabId === tab.id) &&
                dragOverTarget?.tabId === tab.id &&
                dragOverTarget.groupName === groupName
              }
              confirmation={
                deleteConfirmation?.tabId === tab.id
                  ? {
                      hasContent: deleteConfirmation.hasContent,
                      affectedGroupName: deleteConfirmation.affectedGroupName,
                    }
                  : null
              }
              error={tabErrors[tab.id]}
              isHovered={hoveredTabId === tab.id}
              isDuplicatingTab={isDuplicatingTab}
              canDelete={canDeleteTab}
              canDuplicate={canDuplicateTab}
              onDragStart={(e) =>
                dnd.handleDragStart(e, { type: "tab", tabId: tab.id })
              }
              onDragEnd={dnd.handleDragEnd}
              onDragOver={dnd.handleDragOver}
              onDragEnter={(e) =>
                dnd.handleDragEnter(e, { navIndex, tabId: tab.id, groupName })
              }
              onDragLeave={dnd.handleDragLeave}
              onDrop={dnd.handleDrop}
              onNameChange={(value) => onTabNameChange(tab.id, value)}
              onNameBlur={(value) => onTabNameBlur(tab.id, value)}
              onDeleteClick={() => onDeleteTabClick(tab.id)}
              onDuplicateClick={() => onDuplicateTabClick(tab.id, tab.label)}
              onConfirmDelete={onConfirmDeleteTab}
              onCancelDelete={onCancelDeleteTab}
              onMouseEnter={() => onTabMouseEnter(tab.id)}
              onMouseLeave={onTabMouseLeave}
            />
          ))}
          {/* Trailing drop zone: append a dragged tab to the end of this group.
              Only rendered mid-drag so it doesn't add extra height at rest. */}
          {dragged && (
            <Box
              onDragOver={dnd.handleDragOver}
              onDragEnter={(e) =>
                dnd.handleDragEnter(e, { navIndex, tabId: null, groupName })
              }
              onDragLeave={dnd.handleDragLeave}
              onDrop={dnd.handleDrop}
              sx={{
                // The visual line stays thin; the hoverable hit area is much
                // taller so the drop target is actually reachable with a mouse.
                height: "16px",
                mx: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  borderTop: isDraggedOverTrailingZone
                    ? "3px dashed #1976d2"
                    : "3px dashed transparent",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GroupContainer;
